/**
 * DOB re-confirmation banner — DPIA action R4.
 *
 * Surfaces once per 12 months: the backend flag `dob_confirmation_required`
 * goes true 365 days after the last confirmation. User re-enters their date of
 * birth; on success we hide the banner and the 12-month timer resets.
 *
 * This is a non-blocking "snooze" pattern — we do NOT lock the user out of the
 * platform. Hard-blocking would breach Art. 25 (privacy-by-design / least
 * disruption). After a user dismisses, the banner is hidden for the current
 * session via a sessionStorage flag; it returns on next visit until confirmed.
 */
import React, { useState } from "react";
import { ShieldCheck, X } from "lucide-react";
import { toast } from "sonner";
import { axios, API, logError } from "../lib/api";
import { useAuth } from "../auth/AuthProvider";

const SNOOZE_KEY = "toycycle_dob_snoozed";

export const DobReconfirmBanner = () => {
  const { user, refresh } = useAuth();
  const [open, setOpen] = useState(false);
  const [dob, setDob] = useState("");
  const [busy, setBusy] = useState(false);

  if (!user || !user.dob_confirmation_required) return null;
  if (typeof window !== "undefined" && sessionStorage.getItem(SNOOZE_KEY) === user.id) return null;

  const submit = async (e) => {
    e?.preventDefault();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dob)) {
      toast.error("Please enter your date of birth as YYYY-MM-DD.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/account/confirm-dob`, { date_of_birth: dob });
      toast.success("Thanks — confirmed.");
      setOpen(false);
      if (refresh) await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not confirm date of birth.");
      logError("dob-confirm", err);
    } finally {
      setBusy(false);
    }
  };

  const snooze = () => {
    try { sessionStorage.setItem(SNOOZE_KEY, user.id); }
    catch (err) { logError("DobReconfirmBanner: sessionStorage write failed", err); }
    setOpen(false);
  };

  return (
    <>
      {/* Persistent slim banner */}
      <div
        className="bg-[#FFD93D] border-b-2 border-gray-900"
        role="region"
        aria-label="Date of birth reconfirmation"
        data-testid="dob-reconfirm-banner"
      >
        <div className="max-w-7xl mx-auto px-4 py-2 flex flex-wrap items-center justify-between gap-2 text-sm">
          <div className="flex items-center gap-2 min-w-0">
            <ShieldCheck className="w-4 h-4 flex-shrink-0" />
            <span className="font-bold truncate">
              Annual safety check: please re-confirm your date of birth.
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="px-3 py-1 text-xs font-bold rounded-full border-2 border-gray-900 bg-white"
              data-testid="dob-reconfirm-open"
            >
              Confirm now
            </button>
            <button
              type="button"
              onClick={snooze}
              aria-label="Dismiss for this session"
              data-testid="dob-reconfirm-snooze"
              className="p-1"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Modal */}
      {open && (
        <div
          className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="dob-modal-title"
          data-testid="dob-reconfirm-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
        >
          <form
            onSubmit={submit}
            className="w-full max-w-md bg-white border-2 border-gray-900 rounded-2xl shadow-[6px_6px_0px_0px_rgba(17,24,39,1)] p-6"
          >
            <h2 id="dob-modal-title" className="font-bold text-xl mb-2">Confirm your date of birth</h2>
            <p className="text-sm text-gray-600 mb-4">
              We re-check this once a year to keep ToyCycleHub safe for families. Please enter
              the same date of birth you used at registration.
            </p>
            <label className="block text-xs uppercase font-bold text-gray-500 mb-1" htmlFor="dob-input">
              Date of birth (YYYY-MM-DD)
            </label>
            <input
              id="dob-input"
              type="date"
              value={dob}
              onChange={(e) => setDob(e.target.value)}
              className="w-full border-2 border-gray-900 rounded-lg px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#FF6B6B]"
              required
              data-testid="dob-input"
            />
            <div className="flex justify-end gap-2 mt-5">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="px-4 py-2 text-sm font-bold rounded-full border-2 border-gray-900 bg-white"
                data-testid="dob-modal-cancel"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={busy}
                className="px-4 py-2 text-sm font-bold rounded-full border-2 border-gray-900 bg-[#FF6B6B] text-white shadow-[3px_3px_0px_0px_rgba(17,24,39,1)] disabled:opacity-50"
                data-testid="dob-modal-confirm"
              >
                {busy ? "Confirming…" : "Confirm"}
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
};
